/**
 * @file RootRedirect.tsx
 * @brief Bare `/` route handler — sends users to the right place
 *        based on auth + role.
 *
 * @author DES
 * @date 26-05-2026
 */

import { Navigate } from "react-router-dom";
import { useAuth } from "@/shared/auth/auth-context";
import { homePathForUser } from "@/shared/auth/homePath";
import { FullScreenLoader } from "@/components/feedback/FullScreenLoader";
import {
  BOOT_LOADER_MIN_MS,
  useMinimumHold,
} from "@/shared/hooks/useMinimumHold";

export function RootRedirect() {
  const { status, user } = useAuth();
  // "reconnecting": the server could not be reached; the token is kept and
  // retried, never the login page (21-10-2026).
  const authPending = status === "idle" || status === "loading" || status === "reconnecting";
  const showLoader = useMinimumHold(authPending, BOOT_LOADER_MIN_MS);

  if (showLoader) {
    return (
      <FullScreenLoader
        label={status === "reconnecting" ? "Reconnecting…" : "Getting things ready…"}
        immediate={status === "reconnecting"}
      />
    );
  }

  if (status !== "authenticated" || !user) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={homePathForUser(user)} replace />;
}
